/**
 * Access Charity CRM (thankQ) Credential Form
 *
 * Form for configuring Access Charity CRM credentials with:
 * - API Key for X-API-Key header authentication
 * - Bearer Token for OAuth-style authentication
 * - Base URL (defaults to production thankQ API)
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface AccessCharityCrmFormData {
  apiKey: string;
  bearerToken: string;
  baseUrl: string;
}

interface AccessCharityCrmCredentialFormProps {
  formData: AccessCharityCrmFormData;
  onChange: (data: Partial<AccessCharityCrmFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; data?: any; details?: any } | null;
  testing?: boolean;
  onTest?: () => Promise<void>;
}

export function AccessCharityCrmCredentialForm({
  formData,
  onChange,
  isEditing = false,
}: AccessCharityCrmCredentialFormProps) {
  const handleChange = (field: keyof AccessCharityCrmFormData, value: string) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* API Key */}
      <div className="space-y-2">
        <Label htmlFor="apiKey" className="text-sm font-medium">
          API Key <span className="text-muted-foreground text-xs">(Option 1)</span>
        </Label>
        <Input
          id="apiKey"
          type="password"
          placeholder={isEditing ? "Leave unchanged or enter new API key" : "Enter your API key"}
          value={formData.apiKey || ''}
          onChange={(e) => handleChange('apiKey', e.target.value)}
          autoComplete="new-password"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          API Key for X-API-Key header authentication
        </p>
      </div>

      {/* Bearer Token */}
      <div className="space-y-2">
        <Label htmlFor="bearerToken" className="text-sm font-medium">
          Bearer Token <span className="text-muted-foreground text-xs">(Option 2)</span>
        </Label>
        <Input
          id="bearerToken"
          type="password"
          placeholder={isEditing ? "Leave unchanged or enter new token" : "Enter your Bearer token"}
          value={formData.bearerToken || ''}
          onChange={(e) => handleChange('bearerToken', e.target.value)}
          autoComplete="new-password"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          OAuth Bearer token for Authorization header authentication
        </p>
      </div>

      {/* Base URL (Optional) */}
      <div className="space-y-2">
        <Label htmlFor="baseUrl" className="text-sm font-medium">
          Base URL <span className="text-muted-foreground text-xs">(Optional)</span>
        </Label>
        <Input
          id="baseUrl"
          placeholder="https://thankqwebapi.accessacloud.com"
          value={formData.baseUrl || ''}
          onChange={(e) => handleChange('baseUrl', e.target.value)}
          className="text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Custom API base URL. Defaults to production thankQ API if not specified.
        </p>
      </div>
    </div>
  );
}

export default AccessCharityCrmCredentialForm;
